import React, { useState, useRef, useEffect, useMemo } from "react";
import { X, Mail, UserPlus, Send, Users, UserMinus } from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import Avatar from "./Avatar";
import ConfirmationModal from "./ConfirmationModal";

const escapeRegExp = (value) =>
  value.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");

const highlightMatches = (text = "", query) => {
  if (!query || !text) return text;
  const escaped = escapeRegExp(query);
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);
  const queryLower = query.toLowerCase();
  return parts.map((part, idx) => {
    if (part.toLowerCase() === queryLower) {
      return (
        <span key={idx} className="bg-yellow-100 text-secondary-900 px-0.5 rounded">
          {part}
        </span>
      );
    }
    return part;
  });
};

const getUserId = (user) => {
  const rawId = user?._id || user?.id || user?.email || user?.name;
  return rawId ? String(rawId) : null;
};

const InviteUserModal = ({ project, onClose, onUserInvited }) => {
  const { users } = useUser();
  const { addProjectMember, removeProjectMember } = useProject();
  const { showToast } = useNotification();

  const [formData, setFormData] = useState({
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [inviteType, setInviteType] = useState("email"); // "email", "existing", or "add"
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  
  const modalRef = useRef(null);
  const emailInputRef = useRef(null);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close the main modal if a sub-modal (ConfirmationModal) is open
      if (showRemoveConfirm) {
        return;
      }

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, showRemoveConfirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (inviteType !== "email") {
      return;
    }

    if (!formData.email.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await addProjectMember(
        project._id,
        formData.email.trim(),
        "member"
      );
      showToast(
        response.message || "User invited to project successfully!",
        "success"
      );
      if (onUserInvited) {
        onUserInvited();
      }
      onClose();
    } catch (error) {
      console.error("Error inviting user:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to invite user";
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value) => {
    setFormData((prev) => ({ ...prev, email: value }));
    setShowEmailSuggestions(Boolean(value.trim()));
  };

  const handleSuggestionSelect = (email) => {
    setFormData((prev) => ({ ...prev, email }));
    setShowEmailSuggestions(false);
    emailInputRef.current?.focus();
  };

  const confirmRemoveMember = async () => {
    if (!userToRemove || !project?._id) return;

    setIsRemoving(true);
    try {
      await removeProjectMember(project._id, userToRemove._memberId);
      showToast("User removed from project successfully", "success");
      if (onUserInvited) {
        onUserInvited();
      }
    } catch (error) {
      console.error("Error removing user:", error);
      showToast(
        error.response?.data?.message || "Failed to remove user",
        "error"
      );
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirm(false);
      setUserToRemove(null);
    }
  };

  const availableUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const existingMemberIds = new Set();
    (project?.members || []).forEach((member) => {
      if (!member) return;
      if (typeof member === "string") {
        existingMemberIds.add(member);
        return;
      }
      const nestedUser = member.user;
      const id =
        nestedUser?._id ||
        nestedUser?.id ||
        member._id ||
        member.id ||
        null;
      if (id) {
        existingMemberIds.add(id);
      }
    });

    return users.filter(
      (user) =>
        user &&
        !existingMemberIds.has(user._id || user.id)
    ).sort((a, b) => {
      const aName = (a.name || a.email || "").toLowerCase();
      const bName = (b.name || b.email || "").toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [users, project?.members]);

  const projectMembers = useMemo(() => {
    if (!project?.members) return [];
    const seen = new Set();

    return project.members
      .map((member) => {
        if (!member) return null;
        const nestedUser =
          typeof member === "object"
            ? member.user || member
            : null;
        const userId =
          nestedUser?._id ||
          nestedUser?.id ||
          (typeof member === "string" ? member : null);
        if (!userId || seen.has(userId)) return null;
        seen.add(userId);

        return {
          ...nestedUser,
          _memberId: userId,
          _memberRole: member.role || nestedUser?.role,
          _memberJoinedAt: member.joinedAt,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [project?._id, project?.members]);

  const allUsersForSuggestions = useMemo(() => (Array.isArray(users) ? users : []), [users]);
  const emailQuery = formData.email.trim().toLowerCase();
  const emailSuggestions = useMemo(() => {
    if (!emailQuery) return [];
    return allUsersForSuggestions
      .filter(
        (user) =>
          user?.email &&
          user.email.toLowerCase().includes(emailQuery)
      )
      .slice(0, 5);
  }, [allUsersForSuggestions, emailQuery]);

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) => {
      if (!userId) return prev;
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleAddSelectedMembers = async () => {
    if (selectedUserIds.length === 0) {
      showToast("Please select at least one user to add", "error");
      return;
    }

    if (!project?._id) {
      showToast("Unable to add members to this project right now", "error");
      return;
    }

    setIsAddingMembers(true);
    const usersToAdd = availableUsers.filter((user) =>
      selectedUserIds.includes(getUserId(user))
    );
    const results = [];

    for (const user of usersToAdd) {
      try {
        const response = await addProjectMember(
          project._id,
          user.email,
          "member"
        );
        results.push({
          success: true,
          userLabel: user.name || user.email,
          message: response.message || "User added successfully",
        });
      } catch (error) {
        results.push({
          success: false,
          userLabel: user.name || user.email,
          message:
            error.response?.data?.message || "Failed to add user to project",
        });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.length - successCount;

    if (successCount > 0) {
      showToast(
        `${successCount} user${successCount === 1 ? "" : "s"} added successfully`,
        "success"
      );
    }

    if (failCount > 0) {
      showToast(
        `${failCount} user${failCount === 1 ? "" : "s"} failed to add`,
        "error"
      );
    }

    setIsAddingMembers(false);
    setSelectedUserIds([]);
    if (successCount > 0 && onUserInvited) {
      onUserInvited();
    }
  };

  return (
    <div className="modal-overlay">
      <div ref={modalRef} className="modal-content max-w-5xl rounded-lg">
        <div className="bg-[#f1f2f4] dark:bg-slate-900 flex items-center justify-between px-4 h-[72px] min-h-[72px] border-b border-secondary-200 dark:border-slate-700 box-border">
          <div>
            <h2 className="text-xl font-bold text-[#222785] dark:text-slate-200 leading-tight">Invite Users to Project</h2>
            <p className="text-[#131313] text-xs dark:text-slate-400 leading-tight">{project.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400 hover:bg-opacity-20 transition-colors text-gray-600 dark:text-slate-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          {/* Invite Type Tabs */}
          <div className="flex items-center space-x-1 mb-4 bg-secondary-100 dark:bg-slate-900 rounded-lg p-1 h-10 min-h-[40px] max-h-[40px] box-border">
            <button
              type="button"
              onClick={() => setInviteType("email")}
              className={`flex-1 h-full flex items-center justify-center space-x-2 px-4 rounded-md transition-colors duration-200 text-xs font-medium ${
                inviteType === "email"
                  ? "bg-white text-gray-700 dark:bg-slate-700 dark:text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 dark:text-white"
              }`}
            >
              <Mail className="w-3.5 h-3.5" />
              <span>Email Invite</span>
            </button>
            <button
              type="button"
              onClick={() => setInviteType("existing")}
              className={`flex-1 h-full flex items-center justify-center space-x-2 px-4 rounded-md transition-colors duration-200 text-xs font-medium ${
                inviteType === "existing"
                  ? "bg-white text-gray-700 dark:bg-slate-700 dark:text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 dark:text-white"
              }`}
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Existing Member's</span>
            </button>
            <button
              type="button"
              onClick={() => setInviteType("add")}
              className={`flex-1 h-full flex items-center justify-center space-x-2 px-4 rounded-md transition-colors duration-200 text-xs font-medium ${
                inviteType === "add"
                  ? "bg-white text-gray-700 dark:bg-slate-700 dark:text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 dark:text-white"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              <span>Add Members</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {inviteType === "email" ? (
              <>
                <div className="relative">
                  <label
                    htmlFor="email"
                    className="block text-xs font-medium text-secondary-700 dark:text-slate-300 mb-1.5"
                  >
                    Email Address *
                  </label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onFocus={() => setShowEmailSuggestions(Boolean(emailQuery))}
                    onBlur={() => setShowEmailSuggestions(false)}
                    className="w-full h-8 px-3 text-xs border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-black dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Enter email address"
                    required
                  />
                  {showEmailSuggestions && emailSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 z-20 mt-1 max-h-48 overflow-y-auto rounded-xl border border-secondary-200 bg-white shadow-lg">
                      {emailSuggestions.map((user) => (
                        <button
                          key={user._id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSuggestionSelect(user.email)}
                          className="w-full px-3 py-2 text-left transition-colors duration-150 hover:bg-secondary-100"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar user={user} size="xs" />
                          <div className="flex flex-col truncate">
                            <span className="text-sm font-medium text-secondary-900 truncate">
                              {highlightMatches(user.name || user.email || "", emailQuery)}
                            </span>
                            <span className="text-xs text-secondary-500 truncate">
                              {highlightMatches(user.email, emailQuery)}
                            </span>
                          </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-medium text-secondary-700 dark:text-slate-300 mb-1.5"
                  >
                    Personal Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value });
                    }}
                    placeholder="Add a personal message to the invitation..."
                    className="w-full p-2.5 text-xs border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
                    rows={3}
                  />
                </div>
              </>
            ) : inviteType === "existing" ? (
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-3">
                  Project Members ({projectMembers.length})
                </label>

                {projectMembers.length === 0 ? (
                  <div className="text-center py-8 bg-secondary-50 dark:bg-slate-800 rounded-lg">
                    <Users className="w-12 h-12 text-secondary-300 dark:text-slate-400 mx-auto mb-3" />
                    <p className="text-secondary-600 dark:text-slate-300">
                      No users have been assigned to this project yet.
                    </p>
                  </div>
                ) : (
                  <div
                    className={`grid gap-2 max-h-60 overflow-y-auto ${
                      projectMembers.length === 1 ? "grid-cols-1" : "grid-cols-2"
                    }`}
                  >
                    {projectMembers.map((user) => (
                      <div
                        key={user._memberId || user._id || user.id}
                        className="flex items-center space-x-3 px-3 h-[72px] min-h-[72px] max-h-[72px] rounded-lg border border-secondary-200 bg-white dark:bg-slate-900 text-secondary-900 dark:text-slate-300 box-border"
                      >
                        <Avatar user={user} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-xs text-secondary-900 truncate dark:text-slate-300">
                              {user.name || user.email || "Unknown User"}
                            </p>
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                                user._memberRole === "admin"
                                  ? "bg-red-100 text-red-700 dark:bg-red-700 dark:bg-opacity-20 dark:text-red-300"
                                  : "bg-blue-100 text-blue-700 dark:bg-blue-700 dark:bg-opacity-20 dark:text-blue-300"
                              }`}
                            >
                              {user._memberRole || "member"}
                            </span>
                          </div>
                          <p className="text-xs text-secondary-500 truncate dark:text-slate-400 mt-0.5">
                            {user.email}
                          </p>
                        </div>
                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => {
                            setUserToRemove(user);
                            setShowRemoveConfirm(true);
                          }}
                          className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors dark:hover:bg-red-700 dark:hover:bg-opacity-20 flex-shrink-0"
                          title="Remove user from project"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-secondary-600 dark:text-slate-300 mb-2">
                  <span>
                    {selectedUserIds.length} selected • {availableUsers.length} available
                  </span>
                  <span className="font-medium text-secondary-700 dark:text-slate-300">
                    {project?.members?.length || 0} current member
                    {(project?.members?.length || 0) === 1 ? "" : "s"}
                  </span>
                </div>

                {availableUsers.length === 0 ? (
                  <div className="text-center py-10 bg-secondary-50 rounded-2xl border border-dashed border-secondary-200 text-secondary-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                    <UserPlus className="w-14 h-14 mx-auto mb-2" />
                    <p>No additional users are available to add.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
                    {availableUsers
                      .map((user) => ({ user, userId: getUserId(user) }))
                      .filter(({ userId }) => Boolean(userId))
                      .map(({ user, userId }) => {
                        const isSelected = selectedUserIds.includes(userId);
                        return (
                          <div
                            key={userId}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleUserToggle(userId)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleUserToggle(userId);
                              }
                            }}
                            className={`flex items-center space-x-3 px-3 h-[72px] min-h-[72px] max-h-[72px] rounded-lg border-2 transition-colors duration-200 cursor-pointer box-border ${
                              isSelected
                                ? "border-primary-200 bg-primary-50 dark:bg-primary-700 dark:bg-opacity-20 dark:border-primary-600 "
                                : "border-secondary-200 hover:border-secondary-300 dark:border-slate-700 dark:hover:border-slate-600"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleUserToggle(userId);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500 cursor-pointer dark:border-slate-600 dark:bg-slate-900 dark:focus:ring-primary-500 flex-shrink-0"
                            />
                            <Avatar user={user} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-xs text-secondary-900 truncate dark:text-slate-300">
                                {user.name || user.email || "Unknown User"}
                              </p>
                              <p className="text-xs text-secondary-500 truncate dark:text-slate-400 mt-0.5">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="h-8 min-h-[32px] px-4 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 text-xs font-medium transition-colors flex items-center justify-center"
                disabled={loading || isAddingMembers}
              >
                Cancel
              </button>
              {inviteType === "existing" ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="h-8 min-h-[32px] px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium transition-colors flex items-center justify-center space-x-1.5"
                  disabled={loading}
                >
                  <span>Done</span>
                </button>
              ) : (
                <button
                  type={inviteType === "add" ? "button" : "submit"}
                  className="h-8 min-h-[32px] px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium transition-colors flex items-center justify-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    inviteType === "add"
                      ? selectedUserIds.length === 0 || isAddingMembers
                      : loading
                  }
                  onClick={
                    inviteType === "add"
                      ? handleAddSelectedMembers
                      : undefined
                  }
                >
                  {inviteType === "add" ? (
                    isAddingMembers ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <UserPlus className="w-3.5 h-3.5" />
                        <span>Add to Project</span>
                      </>
                    )
                  ) : loading ? (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>
                        {inviteType === "email" ? "Send Invitation" : "Add Users"}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      {showRemoveConfirm && (
        <ConfirmationModal
          isOpen={showRemoveConfirm}
          title="Remove User"
          message="Are you sure you want to remove this user from this project?"
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={confirmRemoveMember}
          onClose={() => {
            setShowRemoveConfirm(false);
            setUserToRemove(null);
          }}
          type="danger"
          isLoading={isRemoving}
        />
      )}
    </div>
  );
};

export default InviteUserModal;
